import { eventDrawingDate, formatDrawingDate, showDrawingDeadline, drawingInputSquare, billingSquareLabel } from '../../utils/drawingPresentation';
import EventMediaPanel from '../../home_media_ui/EventMediaPanel';
import { drawingSubmissionError, drawingCompletionBlockReason, validDrawingSquare } from '../../utils/drawingSubmission';
import { formatRating } from '../../utils/rating';
import { currentServiceInputs, invalidInputServices, serviceInputSignature } from './drawingServiceInputs';
import UnreadChatBadge from '../../components/UnreadChatBadge';
import AdditionalMeasurementPanel from '../../components/Quality/AdditionalMeasurementPanel';
import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { LayoutContext } from "../../context";

import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import AdsClickRoundedIcon from '@mui/icons-material/AdsClickRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid'; // Подключение вида "месяц"
import timeGridPlugin from '@fullcalendar/timegrid'; // Подключение вида "неделя/день"
import interactionPlugin from '@fullcalendar/interaction'; // Для обработки событий клика
import { AspectRatio, Box, Button, Card, CardContent, Chip, FormControl, FormLabel, Grid, IconButton, Input, Link, List, ListItem, Sheet, Stack, Tooltip, Typography } from "@mui/joy";
import { APIDraftsmanChangeSave, APIDraftsmanSetEvent, APIGetEmployeeReviews, APIGetEmployeeReviewsDetails, APIGetEventAdditionalServices, APIQualityAcceptRework } from "../../api";
import { motion } from "framer-motion";
import DraftsmanReworkPanel from "../../components/Quality/DraftsmanReworkPanel";
import { qaMeta } from "../../components/Quality/qualityTheme";
import AddedByChip from "../../components/AddedByChip";
import ModalDraftsmanAddService from "../../components/Modals/ModalDraftsmanAddService";
import StarRating from "../../components/StarRating";
import QualityReviewPanel from "./QualityReviewPanel";
import { ListItemText, Rating } from "@mui/material";
import PaymentBreakdown from "../../components/Payments/PaymentBreakdown";
import { useNavigate } from "react-router";
import { AmountAgreementControl } from "../../components/AmountAgreement";
import { useOpenEventChat } from "../../hooks/useOpenEventChat";


// «Город, адрес» одной строкой (как в календаре): берём готовое поле сервера,
// иначе склеиваем из города и адреса.
const dealFullAddress = (deal) =>
  deal?.full_address
  || [deal?.city?.name, deal?.address].filter(Boolean).join(', ')
  || '';




// ─── Хелпер: форматирование строки доп. услуги для отображения в чипе ────────
//
// Принимает объект услуги события (AddEventServiceItem) и площадь.
// Возвращает строку вида:
//   "Электрика (80 м² × 16 = 1280 ₽)"        — для per_sqm
//   "Доставка ключей (фикс 3000 ₽)"           — для fixed
//   "Зимняя надбавка (+20%)"                   — для percent
//   "Развёртки стен (35 м.п. × 21 = 735 ₽)"   — для per_input с заполненным значением
//   "Развёртки стен (21 ₽/м.п., не заполнено)"— для per_input без значения

function formatDraftsmanService(service, square) {
    if (!service) return '';

    // Название услуги: либо системная (через additional_service.title), либо кастомная (custom_title)
    const title = service.additional_service?.title || service.custom_title || 'Услуга';

    // Цена для чертёжника: custom_draftsman_price имеет приоритет, иначе из связанной услуги
    const drftPrice = parseFloat(
        service.custom_draftsman_price ?? service.additional_service?.draftsman_price ?? 0
    ) || 0;

    // Тип расчёта: явное поле → fallback на calculation_type из additional_service
    //              → fallback на старое full_price_volume для обратной совместимости
    const calcType = service.calculation_type
        || service.additional_service?.calculation_type
        || (
            (service.full_price_volume || service.additional_service?.full_price_volume)
                ? 'fixed' : 'per_sqm'
        );

    // Единица для per_input: либо у самой услуги события, либо у справочной услуги
    const inputUnit = service.input_unit || service.additional_service?.input_unit || 'ед.';
    const inputValue = service.input_value; // заполняет чертёжник при сдаче

    const sq = Number(square) || 0;
    const fmt = (n) => parseFloat(n).toFixed(0); // округление без копеек

    switch (calcType) {
        case 'fixed':
            return `${title} (фикс ${fmt(drftPrice)} ₽)`;

        case 'percent':
            return `${title} (+${drftPrice}%)`;

        case 'per_input': {
            if (inputValue && Number(inputValue) > 0) {
                const total = Number(inputValue) * drftPrice;
                return `${title} (${inputValue} ${inputUnit} × ${fmt(drftPrice)} = ${fmt(total)} ₽)`;
            }
            return `${title} (${fmt(drftPrice)} ₽/${inputUnit}, не заполнено)`;
        }

        case 'per_sqm':
        default: {
            const total = sq * drftPrice;
            return `${title} (${sq} м² × ${fmt(drftPrice)} = ${fmt(total)} ₽)`;
        }
    }
}




function getDeadlineState(deadline, now = Date.now()) {
  if (!deadline) {
    return {
      label: "Не указан",
      caption: "Дедлайн не назначен",
      color: "neutral",
      isOverdue: false,
    };
  }

  const deadlineMs = new Date(deadline).getTime();
  if (!Number.isFinite(deadlineMs)) {
    return {
      label: "Некорректная дата",
      caption: "Проверьте дедлайн",
      color: "warning",
      isOverdue: false,
    };
  }

  const diff = deadlineMs - now;
  const absDiff = Math.abs(diff);
  const totalMinutes = Math.floor(absDiff / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts = [];

  if (days) parts.push(`${days} д`);
  if (hours || days) parts.push(`${hours} ч`);
  parts.push(`${minutes} мин`);

  const isOverdue = diff < 0;
  const color = isOverdue ? "danger" : diff <= 6 * 60 * 60000 ? "danger" : diff <= 24 * 60 * 60000 ? "warning" : "success";

  return {
    label: isOverdue ? `Просрочено на ${parts.join(" ")}` : `Осталось ${parts.join(" ")}`,
    caption: isOverdue ? "Дедлайн вышел" : "До дедлайна",
    color,
    isOverdue,
  };
}

function DeadlineTimer({ deadline, formatDate, isCompleted = false }) {
  const [now, setNow] = useState(Date.now());
  const deadlineState = useMemo(() => getDeadlineState(deadline, now), [deadline, now]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Sheet
      variant="soft"
      color={isCompleted ? "neutral" : deadlineState.color}
      sx={{
        p: 1.25,
        borderRadius: "lg",
        border: "1px solid",
        borderColor: `${isCompleted ? "neutral" : deadlineState.color}.200`,
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography level="title-md">Дедлайн выдачи чертежа:</Typography>
          {isCompleted && (
            <Chip size="sm" variant="soft" color="success">
              Завершено
            </Chip>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" sx={{ gap: 0.75, flexWrap: "wrap" }}>
          <Chip
            variant="plain"
            color="primary"
            startDecorator={<CalendarMonthIcon sx={{ fontSize: 'lg' }} />}
            sx={{
              fontSize: 'md',
              wordBreak: 'break-all',
              "--Chip-minHeight": "30px",
              "--Chip-paddingInline": "15px",
            }}
          >
            {deadline ? formatDate(deadline) : "Не указан"}
          </Chip>

          {deadline && (
            <Chip
              variant="solid"
              color={isCompleted ? "neutral" : deadlineState.color}
              startDecorator={<AccessTimeRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{
                fontWeight: 800,
                "--Chip-minHeight": "30px",
                "--Chip-paddingInline": "14px",
              }}
            >
              {isCompleted ? 'Работа завершена' : deadlineState.label}
            </Chip>
          )}
        </Stack>

        {deadline && !isCompleted && (
          <Typography level="body-xs" color={deadlineState.color} sx={{ fontWeight: 700 }}>
            {deadlineState.caption}
          </Typography>
        )}
      </Stack>
    </Sheet>
  );
}


export default function ItemDealBlock({ item, onChanged, embedded = false }) {
  const { listEvents, modalEvent, setChatModal, updateListEvents, getDeals, account, setModalReviewEmployee, notify, tokenEmployee } = useContext(LayoutContext);
  const { openEventChat } = useOpenEventChat();

  // Мастер по качеству без роли чертёжника — только смотрит (плашка «кто взял»).
  // Если у него ЕСТЬ роль чертёжника — он может брать/завершать, как обычный чертёжник.
  const _positions = account?.position || [];
  const isQualityMasterRole = _positions.includes('quality_master');
  const isDraftsmanRole = _positions.includes('draftsman');
  // В общей подборке право просмотра не означает право брать работу.
  const canDoDraftsmanActions = isDraftsmanRole || (!embedded && !isQualityMasterRole);

  // Это МОЁ событие как чертёжника? Брать/завершать можно только свои.
  const isMyDraftsmanEvent = !!item?.draftsmen_event?.draftsmen?.id
      && Number(item.draftsmen_event.draftsmen.id) === Number(account?.id);

  // ── Контроль качества ───────────────────────────────────────────────────
  const qa = item?.quality || null;
  const qaNeedsRework = !!qa?.needs_rework;              // вернули, ещё не принял
  const qaInRework = !!qa?.rework_in_progress;           // принял, дорабатывает
  const qaMetaShort = (qa?.state && qa.state !== 'passed') ? qaMeta(qa.state) : null;

  // Принять доработку: событие возвращается в черчение, кнопка внизу
  // становится «Завершить доработку».
  const acceptRework = async () => {
    const res = await APIQualityAcceptRework(item.id, tokenEmployee, qa?.round ?? item.qa_round);
    if (res?.status !== 'ok') {
      notify?.(res?.message || res?.detail || 'Не удалось принять доработку', 'danger');
      updateListEvents?.('drawing', account?.id);
      onChanged?.();
      return res;
    }
    notify?.('Доработка принята — можно приступать', 'success');
    updateListEvents?.('drawing', account?.id);
    onChanged?.();
  };

  const navigate = useNavigate();

  const [ square, setSquare ] = useState(item?.bitrix_deal?.square);
  const [ loadBtn, setLoadBtn ] = useState(false);
  const [completionError, setCompletionError] = useState('');
  const [mediaBusy, setMediaBusy] = useState(false);
  const canReadMedia = !!item?.media_access?.read;
  const completionBlockReason = drawingCompletionBlockReason(item, mediaBusy);
  useEffect(() => {
    setCompletionError(''); setMediaBusy(false);
  }, [item?.id, qa?.state]);
  const [ employeeRating, setEmployeeRating ] = useState(null);
  const [ loadingRating, setLoadingRating ] = useState(false);
  const [ serviceInputs, setServiceInputs ] = useState({});
  const submissionFlight = useRef(false);
  const inputSignature = serviceInputSignature(item?.add_event_services);
  useEffect(() => {
    setServiceInputs(currentServiceInputs(item?.add_event_services, {},
      item?.draftsmen_event?.recovery_mode ? item.draftsmen_event.actual_service_inputs || {} : {}));
    // Сбрасываем ввод только при смене задания или версии повторного черчения.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, item?.draftsmen_event?.recovery_revision]);
  useEffect(() => {
    setServiceInputs(previous => currentServiceInputs(item?.add_event_services, previous,
      item?.draftsmen_event?.recovery_mode ? item.draftsmen_event.actual_service_inputs || {} : {}));
    // Сигнатура содержит только поля формы, а не меняющиеся ссылки массивов.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, inputSignature]);
  const [ showBreakdown, setShowBreakdown ] = useState(false);
  const [ addServiceOpen, setAddServiceOpen ] = useState(false);

  // После добавления допа — подтягиваем свежий список допов события и обновляем
  // карточку точечно (без полной перезагрузки списка). При сбое — общий рефреш.
  const refreshEventServices = async () => {
    const eid = item?.id;
    if (!eid) { onChanged?.(null); return; }
    try {
      const fresh = await APIGetEventAdditionalServices(eid);
      if (Array.isArray(fresh)) onChanged?.({ ...item, add_event_services: fresh });
      else onChanged?.(null);
    } catch {
      onChanged?.(null);
    }
  };

  const [tzExpanded, setTzExpanded] = useState(false);

  const [employeeReviews, setEmployeeReviews] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  
  useEffect(() => {
    if (item?.draftsmen_event)
      setSquare(drawingInputSquare(item.draftsmen_event));
    else
      setSquare(item?.bitrix_deal?.square);
  }, [item]);

  // Загрузка рейтинга сотрудника при монтировании компонента
  useEffect(() => {
    if (item?.employee?.id) {
      loadEmployeeRating(item.employee.id);
    }
  }, [item?.employee?.id]);

  const loadEmployeeRating = async (employeeId) => {
    setLoadingRating(true);
    try {
      if (item?.employee?.average_rating != null) {
        // Быстрый путь: рейтинг уже пришёл в списке (лёгкое поле с сервера).
        setEmployeeRating(item.employee.average_rating);
      }
      else if (item?.employee?.reviews) {
        setEmployeeRating(item?.employee?.reviews?.statistics.average_rating);
      }
      else {
        const data = await APIGetEmployeeReviews(employeeId);
        if (data?.results?.statistics?.average_rating != null || data?.results?.statistics?.average_rating != undefined) {
          setEmployeeRating(data.results.statistics.average_rating);
        }
      }
    } catch (error) {
      console.error("Error loading employee rating:", error);
    } finally {
      setLoadingRating(false);
    }
  };

  const loadEmployeeReviews = async (employeeId) => {
    if (!employeeId) return;
    
    setReviewsLoading(true);
    try {
      const data = await APIGetEmployeeReviewsDetails(employeeId);
      console.log("dataaaaa", data);
      if (data) {
        setEmployeeReviews(data);
      }
    } catch (error) {
      console.error("Error loading employee reviews:", error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const ReviewsTooltip = ({ reviews, statistics, loading }) => {
    if (loading) {
      return <Typography>Загрузка отзывов...</Typography>;
    }

    if (!reviews || reviews.length === 0) {
      return <Typography>Пока нет отзывов</Typography>;
    }

    return (
      <Box sx={{ maxWidth: 500, maxHeight: 500, p: 1, overflow: 'auto' }}>
        {/* Статистика */}
        <Box sx={{ mb: 2, p: 1, backgroundColor: 'background.level1', borderRadius: 'sm' }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>Статистика отзывов:</Typography>
          <Typography level="body-sm">Всего отзывов: {statistics.total_reviews}</Typography>
          <Typography level="body-sm">Общий рейтинг: {formatRating(statistics.average_rating)}/5</Typography>
          <Typography level="body-sm">Качество ТЗ: {formatRating(statistics.average_quality_TZ)}/5</Typography>
          <Typography level="body-sm">Качество работы: {formatRating(statistics.average_quality)}/5</Typography>
          <Typography level="body-sm">Отклик: {formatRating(statistics.average_response)}/5</Typography>
        </Box>

        <Typography level="title-sm" sx={{ mb: 1 }}>Последние отзывы:</Typography>
        <List size="sm" sx={{ '--ListItem-paddingY': '0.5rem' }}>
          {reviews.slice(0, 5).map((review, index) => (
            <ListItem key={index} sx={{ borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignContent: "center", justifyContent: 'space-between' }}>
              {/* <ListItemText> */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Chip size="sm" color="primary">
                  {review.employee_leaving_name || 'Аноним'}
                </Chip>
              </Box>
              {/* {review.comment && ( */}
                <Typography color={ review.comment ? "neutral" : "warning" } level="body-xs" sx={{ fontStyle: 'italic', mb: 0.5 }}>
                  {review.comment ? `"${review.comment}"` : "-- Отсутствует комментарий --"}
                </Typography>
              {/* )} */}
              <Typography level="body-xs" color="neutral">
                {new Date(review.date_create).toLocaleDateString('ru-RU')}
              </Typography>
              {/* </ListItemText> */}
            </ListItem>
          ))}
        </List>
        
        {reviews.length > 5 && (
          <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
            ... и еще {reviews.length - 5} отзывов
          </Typography>
        )}
      </Box>
    );
  };

  const handleOpenReviewModal = () => {
    setModalReviewEmployee({
      open: true,
      event: item,
      employee_leaving: account.id,
      about_employee: item.employee.id,
      about_employee_name: item.employee.name
    });
  };

  async function SetDraftsman(eventId) {
    if (!window.confirm('Вы уверены, что хотите взять эту сделку?')) return;
    
    setLoadBtn(true);
    const __set = { event: eventId, draftsmen: account?.id };
    
    try {
        const data = await APIDraftsmanSetEvent(__set, tokenEmployee);
        if (data?.status === "ok") {
            // Если onChanged есть — обновляем точечно карточку
            if (onChanged) {
                // Если бэк вернул обновлённое событие — используем его, иначе триггерим рефреш
                onChanged(data?.event || null);
            } else {
                // Старая логика для совместимости с другими страницами
                updateListEvents?.("drawing", account?.id);
                getDeals?.();
            }
        } else if (data?.response?.data?.message) {
            alert(data.response.data.message);
        }
    } catch (e) {
        console.error('SetDraftsman error', e);
    } finally {
        setLoadBtn(false);
    }
  }



  async function SaveDraftsmanData(eventId, review = null) {
    if (!validDrawingSquare(square)) {
        const message = "Укажите положительную целую площадь (до 10 000 000 м²).";
        if (review) return { status: "error", message };
        alert(message);
        return false;
    }
    
    if (!review && (item?.surveyor_review_required ?? item?.can_leave_surveyor_review ?? true)) {
      setModalReviewEmployee({ open: true, event: item, about_employee: item?.employee?.id,
        about_employee_name: [item?.employee?.last_name, item?.employee?.name].filter(Boolean).join(' '),
        requiredForCompletion: true, onSubmit: data => SaveDraftsmanData(eventId, data) });
      return false;
    }
    if (submissionFlight.current) return false;
    submissionFlight.current = true;
    setLoadBtn(true);
    try {
        const freshServices = await APIGetEventAdditionalServices(item.id, tokenEmployee);
        if (!Array.isArray(freshServices)) {
            return { status: 'error', message: 'Не удалось проверить список услуг. Повторите попытку, когда восстановится связь.' };
        }
        const inputs = currentServiceInputs(freshServices, serviceInputs,
            item?.draftsmen_event?.recovery_mode ? item.draftsmen_event.actual_service_inputs || {} : {});
        if (serviceInputSignature(freshServices) !== serviceInputSignature(item?.add_event_services)) {
            setServiceInputs(inputs);
            if (onChanged) onChanged({ ...item, add_event_services: freshServices });
            else updateListEvents?.('drawing', account?.id);
            return { status: 'error', message: 'Список услуг обновился. Вернитесь к карточке, проверьте поля и нажмите «Завершить» ещё раз.' };
        }
        const missing = invalidInputServices(freshServices, inputs);
        if (missing.length) {
            const titles = missing.map(s => s.input_label || s.custom_title || s.additional_service?.title || `#${s.id}`).join(', ');
            return { status: 'error', message: `Заполните положительные значения для услуг: ${titles}` };
        }
        const __set = {
            square: Number(square), review,
            draftsmen: account?.id,
            service_inputs: inputs,
            recovery_revision: item?.draftsmen_event?.recovery_revision,
            materials_url: `${window.location.origin}/storage/events/${item.id}/drawings`,
        };
        const data = await APIDraftsmanChangeSave(eventId, __set, tokenEmployee);
        if (data?.status === "ok") {
            // A refresh failure must not turn an accepted submission into an error.
            try {
                if (onChanged) onChanged(data?.event || null);
                else {
                    updateListEvents?.("drawing", account?.id);
                    getDeals?.();
                }
            } catch (refreshError) {
                console.warn('Чертёж сдан; обновите список событий', refreshError);
            }
            return true;
        }
        return { status: 'error', message: drawingSubmissionError(data) };
    } catch (e) {
        console.error('SaveDraftsmanData error', e);
        return { status: 'error', message: drawingSubmissionError(e) };

    } finally {
        submissionFlight.current = false;
        setLoadBtn(false);
    }
  }

  function formatPhoneNumber(phone) {
    if (!phone) return null;
    
    // Удаляем все нецифровые символы
    let cleaned = phone.replace(/\D/g, '');
    
    // Если номер начинается с 8 (российский), заменяем на 7
    if (cleaned.startsWith('8')) {
      cleaned = '7' + cleaned.substring(1);
    }
    
    // Если номер не начинается с +, добавляем +
    if (!cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }
    
    return cleaned;
  }


  const formatDate = formatDrawingDate;



  // ── Сумма приходит с сервера ─────────────────────────────────────────
  // Раньше здесь был локальный калькулятор: он знал calculation_type,
  // но не применял минималку уровня чертёжника, компаундил проценты
  // от бегущего итога и использовал старую семантику percent_base.
  // Из-за этого чертёжник видел третье число — не то, что у менеджера,
  // и не то, что платит система.
  const totalDraftsman = Number(
    item?.draftsmen_event?.payment_events?.total_draftsman
    ?? item?.draftsmen_event?.payment_events?.draftsman_salary
    ?? 0
  );

  const draftsmanRows = item?.draftsmen_event?.payment_events?.breakdown_rows?.draftsman || null;




  // ─── Компактная карточка для свободных событий в статусе "Черчение" ───
  const isFreeForTake =
    item?.state === 'drawing'
    && !item?.draftsmen_event
    && canDoDraftsmanActions;   // Задача 2: КК+чертёжник тоже может брать

  if (isFreeForTake) {
    return (
      <Grid xs={12} sm={embedded ? 12 : 6} md={embedded ? 12 : 6} lg={embedded ? 12 : 3} xl={embedded ? 12 : 4} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
        <Card sx={{ width: 'auto', maxWidth: '100%', minWidth: 0, overflow: 'hidden', gap: 1.25 }}>
          {/* ── Шапка: номер + адрес + дата ── */}
          <Box>
            <Typography level="h4" sx={{ lineHeight: 1.1 }}>
              Замер №{item?.bitrix_deal?.id}
            </Typography>
            <Typography level="body-md">{dealFullAddress(item?.bitrix_deal)}</Typography>
            <Typography level="body-sm">{eventDrawingDate(item)}</Typography>
            <Stack direction={"row"} spacing={1} sx={{ mt: 1 }}>
              <Typography level="body-sm">Статус:</Typography>
              <Chip variant="soft" color="success">{item?.name_state}</Chip>
            </Stack>
          </Box>

          {/* ── Инфо-блок ── */}
          <Card variant="soft" sx={{ p: 1.5, gap: 1 }}>
            <CardContent sx={{ gap: 1 }}>
              {showDrawingDeadline(item) && (
                <DeadlineTimer
                  deadline={item?.drawing_deadline}
                  formatDate={formatDate}
                />
              )}

              {/* Программы */}
              {item?.bitrix_deal?.program_for_draftsman?.length > 0 && (
                <>
                  <Typography level="title-md">Программы:</Typography>
                  <Stack direction={'row'} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {item.bitrix_deal.program_for_draftsman.map((_itm, idx) => (
                      <Chip key={idx} variant="plain" color="warning">
                        {_itm?.name}
                      </Chip>
                    ))}
                  </Stack>
                </>
              )}

              {/* ТЗ — сворачиваемое */}
              {item?.bitrix_deal?.tz && (
                <Box>
                    <Typography level="title-md">ТЗ:</Typography>
                    <Typography
                        level={'body-sm'}
                        sx={{
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: tzExpanded ? 'unset' : 3,
                            overflow: 'hidden',
                            transition: 'max-height 0.3s ease',
                        }}
                    >
                        {item.bitrix_deal.tz}
                    </Typography>

                    {/* Кнопка "Показать полностью / Свернуть" — показываем только если текст длинный */}
                    {item.bitrix_deal.tz.length > 150 && (
                        <Button
                            variant="plain"
                            size="sm"
                            color="primary"
                            onClick={() => setTzExpanded(v => !v)}
                            endDecorator={
                                <ExpandMoreRoundedIcon
                                    sx={{
                                        transition: 'transform 0.25s',
                                        transform: tzExpanded ? 'rotate(180deg)' : 'rotate(0)',
                                    }}
                                />
                            }
                            sx={{
                                mt: 0.5,
                                px: 0.5,
                                minHeight: 'auto',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                '--Button-paddingInline': '6px',
                            }}
                        >
                            {tzExpanded ? 'Свернуть' : 'Показать полностью'}
                        </Button>
                    )}
                </Box>
              )}

              {/* Площадь из Bitrix (только для чтения, не инпут) */}
              {item?.bitrix_deal?.square ? (
                <>
                  <Typography level="title-md">Площадь в Bitrix:</Typography>
                  <Typography level={'body-md'} sx={{ fontWeight: 700 }}>
                    {item.bitrix_deal.square} м²
                  </Typography>
                </>
              ) : null}
            </CardContent>
          </Card>

          {/* ── Кнопка "Взять" ── */}
          <Button
            variant="solid"
            size="lg"
            color="primary"
            loading={loadBtn}
            onClick={() => SetDraftsman(item?.id)}
          >
            Взять
          </Button>
        </Card>
      </Grid>
    );
  }
  // ─── Иначе — полная карточка (ниже существующий return) ───

  return (
    <Grid xs={12} sm={embedded ? 12 : 6} md={embedded ? 12 : 6} lg={embedded ? 12 : 4} xl={embedded ? 12 : 3} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Card
        component={motion.div}
        layout
        // Карточка на доработке пульсирует красным контуром. Пульсация тенью,
        // а не размером: карточки стоят в сетке, и скачущие габариты дёргали
        // бы соседей.
        animate={qaNeedsRework ? {
          boxShadow: [
            '0 0 0 0 rgba(220,38,38,0.40)',
            '0 0 0 12px rgba(220,38,38,0)',
            '0 0 0 0 rgba(220,38,38,0)',
          ],
        } : { boxShadow: '0 0 0 0 rgba(0,0,0,0)' }}
        transition={qaNeedsRework
          ? { duration: 2, repeat: Infinity, ease: 'easeOut' }
          : { duration: 0.2 }}
        sx={{
          width: "auto",
          borderColor: qaNeedsRework ? '#fca5a5' : undefined,
          borderWidth: qaNeedsRework ? 1.5 : undefined,
        }}
      >
        <div>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography level="h4" sx={{ lineHeight: 1.1 }}>Замер №{item?.bitrix_deal?.id}</Typography>
              <Typography level="body-md">{dealFullAddress(item?.bitrix_deal)}</Typography>
              <Typography level="body-sm">{eventDrawingDate(item)}</Typography>
            </Box>
            {qaMetaShort && (
              <Chip size="sm" variant="soft"
                sx={{
                  flexShrink: 0, fontWeight: 700, fontSize: '0.66rem',
                  bgcolor: qaMetaShort.soft, color: qaMetaShort.text,
                  border: '1px solid', borderColor: qaMetaShort.border,
                }}>
                {qaMetaShort.short}
              </Chip>
            )}
          </Stack>
          <Stack direction={"row"} spacing={1} sx={{ my: 1 }}>
            <Typography level="body-sm">Статус: </Typography>
            <Chip variant="soft" color="success">
              {item?.name_state}
            </Chip>
          </Stack>

          {/* Контроль качества: что сейчас с чертежом и что от меня нужно */}
          <AdditionalMeasurementPanel eventId={item.id} cycle={item.additional_measurement} />
          <DraftsmanReworkPanel
            quality={item?.quality}
            onAccept={isMyDraftsmanEvent ? acceptRework : undefined}
            onChat={(chatId) => setChatModal({
              open: true, chatId,
              chatName: `Контроль качества · №${item?.bitrix_deal?.id || item?.id}`,
            })}
          />

          <Card variant="soft" sx={{ p: 1.5 }}>
            <CardContent>
              {showDrawingDeadline(item) && (
                <DeadlineTimer
                  deadline={item?.drawing_deadline}
                  formatDate={formatDate}
                />
              )}

              {canReadMedia ? <EventMediaPanel eventId={item.id} token={tokenEmployee}
                showStorageLink={!!item.media_access?.storage_link}
                uploadEnabled={isMyDraftsmanEvent && !!item.media_access?.drawings_write}
                completionBusy={loadBtn} onBusyChange={setMediaBusy} /> : <Typography level="body-sm" sx={{my: 1, color: 'text.tertiary'}}>
                {qaNeedsRework ? 'Файлы откроются после принятия доработки.' : 'Материалы откроются после принятия события в работу.'}
              </Typography>}

              <Typography level="title-md">Программы:</Typography>
              <Stack direction={'row'} sx={{ flexWrap: 'wrap', gap: 1 }}>
                { item?.bitrix_deal?.program_for_draftsman?.map((_itm) => (
                  <Chip variant="plain" color="warning">
                    {_itm?.name}
                  </Chip>
                ))}
              </Stack>

              <Typography level="title-md">ТЗ:</Typography>
              <Typography level={'body-sm'} sx={{ wordBreak: 'break-all', lineHeight: 1.2 }}>{item?.bitrix_deal?.tz}</Typography>
              
              {item?.comment && (
                <>
                  <Typography level="title-md" sx={{ color: 'danger.600' }}>
                    💬 Комментарий:
                  </Typography>
                  <Typography
                    level={'body-sm'}
                    sx={{
                      wordBreak: 'break-all',
                      lineHeight: 1.4,
                      p: 1,
                      borderRadius: 'sm',
                      backgroundColor: 'danger.50',
                      border: '1px solid',
                      borderColor: 'danger.200',
                    }}
                  >
                    {item?.comment}
                  </Typography>
                </>
              )}

              {/* Комментарий обмерщика — из анкеты (старый) или из процесса завершения (новый) */}
              {(item?.questionnaire?.comment || item?.surveyor_comment) && (
                <>
                  <Typography level="title-md" sx={{ color: 'warning.600' }}>💬 Комментарий обмерщика:</Typography>
                  <Typography
                    level={'body-sm'}
                    sx={{
                      wordBreak: 'break-all',
                      lineHeight: 1.4,
                      p: 1,
                      borderRadius: 'sm',
                      backgroundColor: 'warning.50',
                      border: '1px solid',
                      borderColor: 'warning.200',
                    }}
                  >
                    {item?.questionnaire?.comment || item?.surveyor_comment}
                  </Typography>
                </>
              )}
          
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography level="title-md">Обмерщик:</Typography>
                {employeeRating !== null && (
                  <Tooltip
                    title={
                      <ReviewsTooltip
                        reviews={employeeReviews?.reviews} 
                        statistics={employeeReviews?.statistics}
                        loading={reviewsLoading}
                      />
                    }
                    placement="top"
                    variant="plain"
                    arrow
                    onOpen={() => {
                      if (!employeeReviews?.reviews && item?.employee?.id) {
                        loadEmployeeReviews(item.employee.id);
                      }
                    }}
                  >
                    <Chip 
                      variant="plain" 
                      color="primary" 
                      startDecorator={<StarRoundedIcon sx={{ color: '#e8d221' }} />} 
                      sx={{ my: 1, cursor: 'pointer' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography level="body-sm">Рейтинг:</Typography>
                        <Typography level="body-sm" color="primary" >
                          ({formatRating(employeeRating)})
                        </Typography>
                        <Typography level="body-sm"startDecorator={<AdsClickRoundedIcon/>}>
                          
                        </Typography>
                      </Box>
                    </Chip>
                  </Tooltip>
                )}
              </Stack>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Chip 
                  onClick={() => item?.employee?.user_telegram?.username ? window.open(`https://t.me/${item?.employee?.user_telegram?.username}`, '_blank') : undefined} 
                  variant="plain" 
                  color="primary" 
                  startDecorator={<BadgeRoundedIcon sx={{ fontSize: 'lg' }}/>} 
                  sx={{ fontSize: 'md', wordBreak: 'break-all', "--Chip-minHeight": "30px", "--Chip-paddingInline": "15px" }}
                >
                  {item?.employee?.last_name} {item?.employee?.name}
                </Chip>
                
                {loadingRating && (
                  <Typography level="body-sm" color="neutral">
                    Загрузка рейтинга...
                  </Typography>
                )}

                {item?.draftsmen_event && item?.employee?.phone && (
                  <Chip 
                    startDecorator={<img style={{ width: 16 }} src="./whatsapp.png"/>}
                    onClick={() => {
                      const formattedPhone = formatPhoneNumber(item?.employee?.phone);
                      if (formattedPhone) {
                        window.open(`https://wa.me/${formattedPhone}`, '_blank');
                      }
                    }}
                    variant="plain" 
                    color="success" 
                    sx={{ fontSize: 'md', wordBreak: 'break-all', "--Chip-minHeight": "30px", "--Chip-paddingInline": "15px", cursor: 'pointer' }}
                  >
                    {item?.employee?.phone}
                  </Chip>
                )}

                {item?.draftsmen_event && (item?.employee?.max_link || item?.employee?.phone) && (
                  <Chip 
                    startDecorator={<img style={{ width: 19, height: 19 }} src="./icon-max.png" />}
                    onClick={() => {
                      if (item?.employee?.max_link) {
                        // Если ссылка уже полная — открываем как есть, иначе достраиваем
                        const link = item.employee.max_link.startsWith('http')
                          ? item.employee.max_link
                          : `https://max.ru/${item.employee.max_link.replace(/^@/, '')}`;
                        window.open(link, '_blank');
                      }
                      // Если max_link нет — клик ничего не делает, показываем только телефон
                    }}
                    variant="plain" 
                    color="primary" 
                    sx={{ 
                      fontSize: 'md', 
                      wordBreak: 'break-all', 
                      "--Chip-minHeight": "30px", 
                      "--Chip-paddingInline": "15px", 
                      cursor: item?.employee?.max_link ? 'pointer' : 'default' 
                    }}
                  >
                    {item?.employee?.max_link ? 'Открыть в МАХ' : item?.employee?.phone}
                  </Chip>
                )}
              </Stack>

              {account?.position?.includes('quality_master') && item?.draftsmen_event?.draftsmen && (
                <>
                  <Typography level="title-md">Чертежник:</Typography>
                  <Chip 
                    variant="plain" 
                    color="primary" 
                    startDecorator={<BadgeRoundedIcon sx={{ fontSize: 'lg' }}/>} 
                    sx={{ fontSize: 'md', wordBreak: 'break-all', "--Chip-minHeight": "30px", "--Chip-paddingInline": "15px" }}
                  >
                    {item.draftsmen_event.draftsmen?.last_name} {item.draftsmen_event.draftsmen.name}
                  </Chip>
                </>
              )}

              {item?.manager && (
                <Stack direction="column" justifyContent="space-between" alignItems="flex-start">
                  <Typography level="title-md">Менеджер ответственный:</Typography>
                  <Chip 
                    variant="plain" 
                    color="primary" 
                    startDecorator={<BadgeRoundedIcon sx={{ fontSize: 'lg' }}/>} 
                    sx={{ fontSize: 'md', mt: 1, wordBreak: 'break-all', "--Chip-minHeight": "30px", "--Chip-paddingInline": "15px" }}
                    onClick={() => item?.manager?.user_telegram?.username ? window.open(`https://t.me/${item?.manager?.user_telegram?.username}`, '_blank') : undefined} 
                  >
                    {item?.manager?.last_name} {item?.manager?.name}
                  </Chip>
                </Stack>
              )}

              <Typography level="title-md">Площадь в Bitrix:</Typography>
              <Typography level={'body-md'} sx={{ wordBreak: 'break-all', lineHeight: 1.2 }}>
                {item?.bitrix_deal?.square} м²
              </Typography>
              
              { item?.draftsmen_event && (
                <>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <Typography level="title-md">Доп. услуги:</Typography>
                        {!item?.draftsmen_event?.is_completed && (
                            <Button size="sm" variant="soft" color="primary" onClick={() => setAddServiceOpen(true)}>
                                + Добавить доп
                            </Button>
                        )}
                    </Stack>
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, width: '100%' }}>
                        { (item.add_event_services || []).map((_itm) => {
                            const calcType = _itm.calculation_type
                                || _itm.additional_service?.calculation_type
                                || ((_itm.full_price_volume || _itm.additional_service?.full_price_volume) ? 'fixed' : 'per_sqm');

                            const chipColor =
                                calcType === 'percent'   ? 'warning' :
                                calcType === 'fixed'     ? 'primary' :
                                calcType === 'per_input' ? 'neutral' :
                                                          'danger';

                            const needsInput = calcType === 'per_input'
                                && (!_itm.input_value || Number(_itm.input_value) <= 0);

                            return (
                                <Stack key={_itm.id} spacing={0.3} sx={{ maxWidth: '100%' }}>
                                    <Chip
                                        variant="soft"
                                        color={chipColor}
                                        size="md"
                                        sx={{
                                            maxWidth: '100%',
                                            '& .MuiChip-label': {
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                lineHeight: 1.3,
                                            },
                                        }}
                                        endDecorator={needsInput && (
                                            <Chip size="sm" variant="solid" color="warning"
                                                sx={{ ml: 0.5, fontSize: '0.65rem', height: 18 }}>
                                                ⚠ ввод
                                            </Chip>
                                        )}
                                    >
                                        {formatDraftsmanService(_itm, item?.bitrix_deal?.square)}
                                    </Chip>
                                    <AddedByChip item={_itm} />
                                </Stack>
                            );
                        })}
                        { (item.add_event_services || []).length === 0 && (
                            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                Допов пока нет
                            </Typography>
                        )}
                    </Stack>
                </>
              )}

              {/* Оценка отделом качества — только по ЧУЖИМ событиям, не по своим */}
              {isQualityMasterRole && item?.draftsmen_event?.draftsmen && !isMyDraftsmanEvent && (
                <QualityReviewPanel
                  item={item}
                  onSaved={(review) => onChanged?.({ ...item, quality_review: review })}
                />
              )}
            </CardContent>
          </Card>

        </div>
        {/* <CardContent orientation="horizontal"> */}
          <Stack direction={'row'} spacing={1} 
            sx={{ 
              alignItems: 'flex-end', 
              justifyContent: 'flex-start',
              flexWrap: 'wrap', gap: 1.5,
              opacity: !item?.draftsmen_event ? 0.5 : 1, // Бледно-серый цвет для завершенных
              pointerEvents: !item?.draftsmen_event ? "none" : "auto", // Блокируем взаимодействие
            }}
          >
            {canDoDraftsmanActions && (
              <FormControl>
                  <FormLabel>{item?.draftsmen_event?.recovery_mode ? 'Фактическая площадь' : 'Площадь'}</FormLabel>
                  {item?.draftsmen_event?.recovery_mode && <Typography level="body-xs" sx={{ maxWidth: 300, mb: 1 }}>
                    Повторное черчение. Расчётная площадь: {billingSquareLabel(item.draftsmen_event.square)}.
                    Фактическая площадь и значения услуг сохраняются отдельно. Сдача исправлений сохраняет финансовые условия; отдельное начисление согласует менеджер.
                  </Typography>}
                  <Input
                    disabled={item?.draftsmen_event?.is_completed}
                    sx={{ width: 100 }}
                    value={square}
                    onChange={(e) => setSquare(e.target.value)} 
                    size="md" required
                  />
              </FormControl>
            )}

            {/* HOME_MEDIA_V2_RECOVERY */}{item?.draftsmen_event?.recovery_mode && canDoDraftsmanActions && <Typography level="body-sm">Добавьте исправленные чертежи в файлы события, затем нажмите «Завершить». Предыдущая выдача сохранится.</Typography>}
            {/* ── Поля ввода для услуг типа per_input ── */}
            {item?.add_event_services
                ?.filter(srv => (srv.calculation_type || srv.additional_service?.calculation_type) === 'per_input')
                ?.map(srv => (
                    <FormControl key={srv.id}>
                        <FormLabel>
                            {srv.input_label || srv.additional_service?.input_label || srv.custom_title || 'Значение'}
                            {(srv.input_unit || srv.additional_service?.input_unit)
                                ? ` (${srv.input_unit || srv.additional_service?.input_unit})`
                                : ''
                            }
                        </FormLabel>
                        <Input
                            type="number"
                            disabled={item?.draftsmen_event?.is_completed}
                            sx={{ width: 130 }}
                            value={serviceInputs[srv.id] ?? srv.input_value ?? ''}
                            onChange={(e) => setServiceInputs(prev => ({ ...prev, [srv.id]: e.target.value }))}
                            size="md"
                            required
                            placeholder="0"
                        />
                    </FormControl>
                ))
            }
            {/* <Button
              variant="soft"
              size="md"
              color="primary"
              sx={{ fontWeight: 600 }}
            >
              Сохранить
            </Button> */}
          </Stack>
        {/* </CardContent> */}


        {canDoDraftsmanActions && (!item?.draftsmen_event?.event || isMyDraftsmanEvent) ? (
          !item?.draftsmen_event?.event ?
            <Button
              variant="solid"
              size="lg"
              color="primary"
              loading={loadBtn}
              onClick={() => SetDraftsman(item?.id)}
            >
              Взять
            </Button>
            :
            !item?.draftsmen_event?.is_completed ? (
              <Stack
                direction={'row'}
                sx={{
                  width: '100%', gap: 1,
                  flexWrap: 'wrap',
                  '& > button': { flex: '1 1 auto', minWidth: 0 },
                }}
              >
                <Button
                  variant="solid"
                  size="lg"
                  color={qaInRework ? 'warning' : 'success'}
                  loading={loadBtn}
                  // Подтверждение ознакомления необязательно; допобмер сначала закрывает обмерщик.
                  disabled={Boolean(completionBlockReason)}
                  sx={{ width: '100%' }}
                  onClick={async () => {
                    setCompletionError('');
                    const result = await SaveDraftsmanData(item?.draftsmen_event?.id);
                    if (result?.status === 'error') setCompletionError(result.message || 'Не удалось завершить работу.');
                  }}
                >
                  {qaInRework || qaNeedsRework ? 'Завершить доработку' : 'Завершить'}
                </Button>
                {completionBlockReason && <Typography level="body-sm" role="status" sx={{width: '100%', color: 'text.secondary', overflowWrap: 'anywhere'}}>
                  {completionBlockReason}
                </Typography>}
                <Button
                  variant="soft"
                  size="lg"
                  color="primary"
                  loading={loadBtn}
                  startDecorator={<UnreadChatBadge chatId={item?.event_chat?.id}><ChatRoundedIcon sx={{ fontSize: 16 }} /></UnreadChatBadge>}
                  onClick={() => openEventChat(item)}
                >
                  В&nbsp;чат
                </Button>
                {item?.client_chat?.id && (
                  <Button
                    variant="soft"
                    size="lg"
                    color="warning"
                    startDecorator={<UnreadChatBadge chatId={item?.client_chat?.id}><ChatRoundedIcon sx={{ fontSize: 16 }} /></UnreadChatBadge>}
                    onClick={() => setChatModal({ open: true, chatId: item.client_chat.id, chatName: item.client_chat.name || `Клиент #${item?.bitrix_deal?.id}` })}
                  >
                    Чат с&nbsp;клиентом
                  </Button>
                )}
              </Stack>
            ) : (
              <Stack
                direction={'column'}
                sx={{
                  width: '100%', gap: 1
                }}
              >
                <Button
                  variant="solid"
                  size="lg"
                  color="neutral"
                  disabled
                  sx={{ flex: item?.can_leave_surveyor_review ? '0 1 auto' : '1 1 auto' }}
                >
                  Завершено
                </Button>
                {item?.can_leave_surveyor_review && (
                  <Button
                    variant="solid"
                    size="lg"
                    color="warning"
                    startDecorator={<StarRoundedIcon sx={{ fontSize: 18 }} />}
                    onClick={handleOpenReviewModal}
                  >
                    Оставить отзыв об обмерщике
                  </Button>
                )}
              </Stack>
            )
        ) : (
          // Для quality_master показываем информативную кнопку
          <Sheet
            variant="solid"
            size="lg"
            sx={{
              p: 1.5,
              borderRadius: 'md',
              textAlign: 'center'
            }}
            color={ item?.draftsmen_event ? "warning" : "danger" }
          >
            {item?.draftsmen_event ? `Взято: ${item.draftsmen_event.draftsmen?.last_name} ${item.draftsmen_event.draftsmen?.name}` : 'Свободно'}
          </Sheet>
        )}

        {completionError && <Typography color="danger" level="body-sm" role="alert" sx={{my: 1}}>{completionError}</Typography>}

        { isMyDraftsmanEvent && totalDraftsman ? 
          (
            <Card variant="soft" color="primary" sx={{ p: 1.5 }}>
              <Box>
                <Typography level="title-lg">Сумма оплаты</Typography>
                  <Typography level={'body-lg'} sx={{ wordBreak: 'break-all', lineHeight: 1.2 }}>
                    {totalDraftsman.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽
                  </Typography>
                  {draftsmanRows && (
                    <Box sx={{ mt: 1 }}>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
                        <Button
                          size="sm" variant="plain" color="primary"
                          onClick={() => setShowBreakdown((v) => !v)}
                          sx={{ px: 0.5, minHeight: 24 }}
                        >
                          {showBreakdown ? "Скрыть расчёт" : "Из чего сумма"}
                        </Button>
                        <Button
                          size="sm" variant="plain" color="neutral"
                          onClick={() => navigate("/my-payments")}
                          sx={{ px: 0.5, minHeight: 24 }}
                        >
                          Все мои выплаты →
                        </Button>
                      </Stack>
                      {showBreakdown && (
                        <Box sx={{ mt: 0.75 }}>
                          <PaymentBreakdown compact rows={draftsmanRows} accent="#0891b2" />
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Согласие чертёжника с суммой */}
                  <AmountAgreementControl
                    eventId={item?.id}
                    role="draftsman"
                    agreement={item?.draftsmen_event?.payment_events?.draftsman_amount_agreement}
                    expectedAmount={item?.draftsmen_event?.payment_events?.draftsman_expected_amount}
                    onChanged={onChanged}
                    notify={notify}
                  />
              </Box>
            </Card>
          )
          :
          undefined
        }

        <ModalDraftsmanAddService
          open={addServiceOpen}
          onClose={() => setAddServiceOpen(false)}
          eventId={item?.id}
          onAdded={() => { refreshEventServices(); }}
        />
      </Card>
    </Grid>
  )
};